import { CSSProperties, useEffect, useId, useState } from "react"
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Download, GripVerticalIcon, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ExportColumn, ExportFormat, ExportOptions, ExportScope } from "./export"

export interface ExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  columns: ExportColumn[]
  initialVisible: string[]
  hasSelection?: boolean
  canExportAll?: boolean
  totalRows?: number
  defaultMaxRows: number
  onExport: (options: ExportOptions) => Promise<void>
}

const formats: { value: ExportFormat, label: string }[] = [
  { value: "csv", label: "CSV" },
  { value: "xlsx", label: "Excel" },
  { value: "md", label: "Markdown" },
]

export function ExportDialog(props: ExportDialogProps) {

  const { open, onOpenChange, columns, initialVisible, hasSelection, canExportAll, totalRows, defaultMaxRows, onExport } = props

  const [format, setFormat] = useState<ExportFormat>("csv")
  const [scope, setScope] = useState<ExportScope>("page")
  const [checked, setChecked] = useState<string[]>(initialVisible)
  const [order, setOrder] = useState<string[]>(columns.map((c) => c.id))
  const [maxRows, setMaxRows] = useState(defaultMaxRows)
  const [busy, setBusy] = useState(false)

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}))

  // The table order and visibility can change between openings, so pick them up on each open
  useEffect(() => {
    if (!open) return
    setOrder(columns.map((c) => c.id))
    setChecked(initialVisible)
    setScope(hasSelection ? "selected" : "page")
    setMaxRows(defaultMaxRows)
    setBusy(false)
  }, [open])

  const scopes: { value: ExportScope, label: string, disabled?: boolean }[] = [
    { value: "page", label: "Current page" },
    { value: "all", label: "All pages", disabled: !canExportAll },
    { value: "selected", label: "Selected rows", disabled: !hasSelection },
  ]

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!active || !over || active.id === over.id) return
    setOrder((current) => arrayMove(current, current.indexOf(active.id as string), current.indexOf(over.id as string)))
  }

  function toggleColumn(id: string, value: boolean) {
    setChecked((current) => value ? [...current, id] : current.filter((c) => c !== id))
  }

  async function run() {
    const byId = new Map(columns.map((c) => [c.id, c]))
    const selectedColumns = order.filter((id) => checked.includes(id)).map((id) => byId.get(id)).filter(Boolean) as ExportColumn[]
    setBusy(true)
    try {
      await onExport({ format, scope, columns: selectedColumns, maxRows })
      onOpenChange(false)
    } finally {
      setBusy(false)
    }
  }

  const allChecked = checked.length === columns.length

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Export</DialogTitle>
        <DialogDescription>Choose the format, which rows to include and which columns to write.</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">

        <div className="grid gap-2">
          <Label>Format</Label>
          <div className="flex gap-2">
            {formats.map((f) => (
              <Button key={f.value} type="button" size="sm" variant={format === f.value ? "default" : "outline"} onClick={() => setFormat(f.value)}>
                {f.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid gap-2">
          <Label>Rows</Label>
          <div className="flex flex-wrap gap-2">
            {scopes.map((s) => (
              <Button key={s.value} type="button" size="sm" disabled={s.disabled} variant={scope === s.value ? "default" : "outline"} onClick={() => setScope(s.value)}>
                {s.label}
              </Button>
            ))}
          </div>
          {scope === "all" && <div className="flex items-center gap-2 pt-1">
            <Label className="whitespace-nowrap text-muted-foreground">Max rows</Label>
            <Input type="number" min={1} className="h-8 w-28" value={maxRows} onChange={(e) => setMaxRows(Math.max(1, Number(e.target.value) || 1))} />
            {totalRows !== undefined && totalRows > maxRows && <span className="text-xs text-amber-600">
              This table has {totalRows} rows, the file will be truncated.
            </span>}
          </div>}
        </div>

        <div className="grid gap-2">
          <div className="flex items-center">
            <Label>Columns</Label>
            <div className="grow"></div>
            <Button type="button" size="sm" variant="ghost" onClick={() => setChecked(allChecked ? [] : columns.map((c) => c.id))}>
              {allChecked ? "Clear all" : "Select all"}
            </Button>
          </div>
          <div className="max-h-64 overflow-y-auto rounded-md border">
            <DndContext id={useId()} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd} sensors={sensors}>
              <SortableContext items={order} strategy={verticalListSortingStrategy}>
                {order.map((id) => {
                  const column = columns.find((c) => c.id === id)
                  if (!column) return null
                  return <SortableColumn key={id} column={column} checked={checked.includes(id)} onToggle={(v) => toggleColumn(id, v)} />
                })}
              </SortableContext>
            </DndContext>
          </div>
          <p className="text-xs text-muted-foreground">Drag to reorder. The file follows this order.</p>
        </div>

      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
        <Button type="button" onClick={run} disabled={busy || checked.length === 0}>
          {busy ? <Loader2 className="animate-spin" /> : <Download />}
          Export
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

}

function SortableColumn({ column, checked, onToggle }: { column: ExportColumn, checked: boolean, onToggle: (value: boolean) => void }) {

  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({ id: column.id })

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
  }

  return <div ref={setNodeRef} style={style} className="flex items-center gap-2 border-b bg-background px-2 py-1.5 last:border-b-0">
    <GripVerticalIcon size={16} className="shrink-0 cursor-grab text-muted-foreground" {...attributes} {...listeners} />
    <Checkbox id={`export-col-${column.id}`} checked={checked} onCheckedChange={(v) => onToggle(v === true)} />
    <Label htmlFor={`export-col-${column.id}`} className="grow cursor-pointer truncate">{column.header}</Label>
  </div>
}
